import { useEffect, useMemo, useRef, useState } from 'react';
import { Question } from '../types';
import { imageCache } from '../utils/imageCache';
import { extractImageKey, resolveSignedImageUrl } from '../utils/signedImageUrl';
import { getPrimaryQuestionImageUrl, isPrimaryQuestionImageReady, preloadImageUrl } from 'components/QuestionFlow/questionImageUtils';

type PreloadStatus = 'idle' | 'loading' | 'ready' | 'error';

const DEFAULT_PRELOAD_COUNT = 3;
const EMPTY_QUESTIONS: Question[] = [];

export interface UseImagePreloaderOptions {
  initialQuestionId?: number | null;
  preloadCount?: number;
  initialImageReady?: boolean;
}

export const useImagePreloader = (
  questions: Question[] | undefined,
  options?: UseImagePreloaderOptions,
) => {
  const { initialQuestionId = null, preloadCount = DEFAULT_PRELOAD_COUNT, initialImageReady = false } = options ?? {};
  const seenKeysRef = useRef(new Set<string>());
  const questionList = useMemo(() => (Array.isArray(questions) ? questions : EMPTY_QUESTIONS), [questions]);

  const initialQuestion = useMemo(() => {
    if (!questionList.length) {
      return null;
    }

    if (typeof initialQuestionId === 'number') {
      return questionList.find((question) => question.id === initialQuestionId) ?? questionList[0];
    }

    return questionList[0];
  }, [initialQuestionId, questionList]);
  const [initialStatus, setInitialStatus] = useState<PreloadStatus>(() => {
    if (initialImageReady || isPrimaryQuestionImageReady(initialQuestion)) {
      return 'ready';
    }

    if (!questionList.length) {
      return 'ready';
    }

    return 'idle';
  });

  useEffect(() => {
    let isActive = true;

    if (!questionList.length) {
      setInitialStatus('ready');
      return () => {
        isActive = false;
      };
    }

    const queue: Question[] = [];
    if (initialQuestion) {
      queue.push(initialQuestion);
    }

    if (preloadCount > 0) {
      for (const question of questionList.slice(0, Math.max(preloadCount, 1))) {
        if (!queue.includes(question)) {
          queue.push(question);
        }
      }
    }

    const initialVisualUrl = getPrimaryQuestionImageUrl(initialQuestion);
    const initialKey = initialVisualUrl ? extractImageKey(initialVisualUrl) : null;
    const shouldForceInitialReady = initialImageReady || isPrimaryQuestionImageReady(initialQuestion);

    if (!initialKey || shouldForceInitialReady) {
      setInitialStatus('ready');
    } else if (imageCache.get(initialKey)) {
      setInitialStatus('ready');
    } else {
      setInitialStatus('loading');
    }

    const seenKeys = seenKeysRef.current;

    const loadQuestionImages = async (question: Question) => {
      const urls: string[] = [];

      const addUrl = (value?: string | null) => {
        if (!value) {
          return;
        }
        const trimmed = value.trim();
        if (!trimmed) {
          return;
        }
        if (urls.indexOf(trimmed) === -1) {
          urls.push(trimmed);
        }
      };

      if (question.media?.media_type === 'image') {
        if (question.media.storage_key) {
          addUrl(question.media.storage_key);
        }
        if (question.media.url) {
          addUrl(question.media.url);
        }
      }

      addUrl(question.visual_component_url);
      addUrl(question.image_url);

      for (let index = 0; index < urls.length; index += 1) {
        const rawUrl = urls[index];
        if (!isActive) {
          return;
        }

        const key = extractImageKey(rawUrl);
        if (!key || seenKeys.has(key)) {
          continue;
        }

        seenKeys.add(key);

        try {
          const signedUrl = await resolveSignedImageUrl(rawUrl);
          if (!isActive) {
            return;
          }

          await preloadImageUrl(signedUrl);

          if (!isActive) {
            return;
          }

          if (key === initialKey && !shouldForceInitialReady) {
            setInitialStatus('ready');
          }
        } catch (error) {
          if (!isActive) {
            return;
          }

          if (key === initialKey && !shouldForceInitialReady) {
            setInitialStatus('error');
          }
        }
      }
    };

    const preload = async () => {
      for (const question of queue) {
        if (!isActive) {
          break;
        }

        await loadQuestionImages(question);
      }

      if (initialKey && isActive && !shouldForceInitialReady) {
        setInitialStatus((status) => (status === 'loading' ? 'ready' : status));
      }

      if ((!initialKey || shouldForceInitialReady) && isActive) {
        setInitialStatus('ready');
      }
    };

    preload();

    return () => {
      isActive = false;
    };
  }, [initialImageReady, initialQuestion, preloadCount, questionList]);

  const isInitialImageReady = initialStatus === 'ready' || initialStatus === 'error';

  return {
    isInitialImageReady,
    initialImageStatus: initialStatus,
  };
};
